import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isNativeApp } from "@/lib/platform";

export interface ExportData {
  exportDate: string;
  userId: string;
  dailyEntries: any[];
  detailedEntries: any[];
  userGoals: any | null;
  customMetrics: any[];
  metricPreferences: any | null;
  metricDefinitions: any[];
  progressSnaps: any[];
}

/**
 * Fetches all user data from Supabase for export.
 */
async function fetchAllUserData(userId: string): Promise<ExportData> {
  // Fetch all data in parallel for speed
  const [
    dailyEntriesRes,
    detailedEntriesRes,
    userGoalsRes,
    customMetricsRes,
    metricPrefsRes,
    metricDefsRes,
    progressSnapsRes,
  ] = await Promise.all([
    // 1. ALL daily entries (no date filter)
    supabase
      .from("daily_entries")
      .select("*")
      .eq("user_id", userId)
      .order("entry_date", { ascending: true }),

    // 2. ALL detailed entry logs
    supabase
      .from("detailed_entries")
      .select("*")
      .eq("user_id", userId)
      .order("entry_date", { ascending: true }),

    // 3. User goals
    supabase
      .from("user_goals")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle(),

    // 4. Custom metrics
    supabase
      .from("custom_metrics")
      .select("*")
      .eq("user_id", userId),

    // 5. Metric preferences (which metrics are activated)
    supabase
      .from("metric_preferences")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle(),

    // 6. All metric definitions (built-in)
    supabase.rpc("get_metric_definitions"),

    // 7. Progress snaps (id, caption/text, date, image_url, metric)
    supabase
      .from("progress_snaps")
      .select("id, metric_name, image_url, text_content, snap_date, created_at")
      .eq("user_id", userId)
      .order("snap_date", { ascending: true }),
  ]);

  // Check for critical errors
  if (dailyEntriesRes.error) throw new Error(`Failed to fetch daily entries: ${dailyEntriesRes.error.message}`);
  if (detailedEntriesRes.error) throw new Error(`Failed to fetch detailed entries: ${detailedEntriesRes.error.message}`);
  if (userGoalsRes.error) throw new Error(`Failed to fetch goals: ${userGoalsRes.error.message}`);
  if (customMetricsRes.error) throw new Error(`Failed to fetch custom metrics: ${customMetricsRes.error.message}`);
  if (metricPrefsRes.error) throw new Error(`Failed to fetch metric preferences: ${metricPrefsRes.error.message}`);
  if (progressSnapsRes.error) throw new Error(`Failed to fetch progress snaps: ${progressSnapsRes.error.message}`);

  return {
    exportDate: new Date().toISOString(),
    userId,
    dailyEntries: dailyEntriesRes.data || [],
    detailedEntries: detailedEntriesRes.data || [],
    userGoals: userGoalsRes.data || null,
    customMetrics: customMetricsRes.data || [],
    metricPreferences: metricPrefsRes.data || null,
    metricDefinitions: metricDefsRes.data || [],
    progressSnaps: progressSnapsRes.data || [],
  };
}

/**
 * Triggers a JSON file download in the browser.
 */
function downloadJsonFile(data: ExportData) {
  const jsonStr = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gitfit-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copies JSON data to clipboard.
 */
async function copyJsonToClipboard(data: ExportData): Promise<boolean> {
  const jsonStr = JSON.stringify(data, null, 2);
  try {
    await navigator.clipboard.writeText(jsonStr);
    return true;
  } catch {
    return false;
  }
}

/**
 * Hook that provides data export functionality.
 */
export function useDataExport(userId: string | undefined) {
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingPhotos, setIsSavingPhotos] = useState(false);
  const [photoProgress, setPhotoProgress] = useState({ current: 0, total: 0, phase: "" as "" | "downloading" | "saving" });

  const exportData = async () => {
    if (!userId) return;

    setIsExporting(true);
    try {
      const data = await fetchAllUserData(userId);

      if (isNativeApp()) {
        // On native mobile: try share, then clipboard fallback
        const jsonStr = JSON.stringify(data, null, 2);
        if (navigator.share) {
          try {
            const file = new File(
              [jsonStr],
              `gitfit-export-${new Date().toISOString().slice(0, 10)}.json`,
              { type: "application/json" }
            );
            await navigator.share({ files: [file] });
            return;
          } catch (shareErr) {
            // User cancelled or share failed — fall through to clipboard
            console.log("Share cancelled or failed, falling back to clipboard");
          }
        }
        // Clipboard fallback
        const copied = await copyJsonToClipboard(data);
        if (copied) {
          toast.success("Data copied to clipboard");
        } else {
          // Last resort: download
          downloadJsonFile(data);
        }
      } else {
        // On web: direct file download
        downloadJsonFile(data);
        toast.success("Export downloaded");
      }
    } catch (err: any) {
      console.error("Export failed:", err);
      toast.error(err?.message || "Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const saveAllProgressPhotos = async () => {
    if (!userId) return;

    setIsSavingPhotos(true);
    try {
      // Fetch all progress snaps with image URLs
      const { data: snaps, error } = await supabase
        .from("progress_snaps")
        .select("id, image_url, metric_name, snap_date")
        .eq("user_id", userId)
        .not("image_url", "is", null)
        .order("snap_date", { ascending: true });

      if (error) throw error;
      if (!snaps || snaps.length === 0) {
        toast.info("No progress photos to save");
        return;
      }

      // Phase 1: Download all images as blobs in memory
      setPhotoProgress({ current: 0, total: snaps.length, phase: "downloading" });
      const files: File[] = [];

      for (let i = 0; i < snaps.length; i++) {
        const snap = snaps[i];
        if (!snap.image_url) continue;

        try {
          const response = await fetch(snap.image_url, { mode: "cors" });
          const blob = await response.blob();
          const ext = snap.image_url.includes(".png") ? "png" : "jpg";
          const filename = `gitfit-${snap.snap_date}-${snap.metric_name}-${snap.id.slice(0, 8)}.${ext}`;
          files.push(new File([blob], filename, { type: blob.type || `image/${ext}` }));
        } catch (fetchErr) {
          console.warn("Skipping image that failed to download:", snap.image_url, fetchErr);
        }
        setPhotoProgress({ current: i + 1, total: snaps.length, phase: "downloading" });
      }

      if (files.length === 0) {
        toast.error("Could not download any photos");
        return;
      }

      // Phase 2: Save files
      setPhotoProgress({ current: 0, total: files.length, phase: "saving" });

      // Try Web Share API — share all files at once (native share sheet)
      if (navigator.share) {
        try {
          // Try all files at once first
          await navigator.share({ files });
          toast.success(`Shared ${files.length} progress photo${files.length !== 1 ? "s" : ""}`);
          return;
        } catch (shareErr: any) {
          if (shareErr?.name === "AbortError") {
            // User cancelled — exit cleanly
            return;
          }
          // Share with all files failed (e.g. too many files) — try one at a time
          console.warn("Bulk share failed, trying one at a time:", shareErr);
          let sharedCount = 0;
          for (let i = 0; i < files.length; i++) {
            try {
              await navigator.share({ files: [files[i]] });
              sharedCount++;
            } catch (singleErr: any) {
              if (singleErr?.name === "AbortError") {
                // User cancelled mid-way — stop and report what was saved
                if (sharedCount > 0) {
                  toast.success(`Saved ${sharedCount} of ${files.length} photos`);
                }
                return;
              }
              console.warn("Single file share failed:", files[i].name, singleErr);
            }
            setPhotoProgress({ current: i + 1, total: files.length, phase: "saving" });
          }
          if (sharedCount > 0) {
            toast.success(`Saved ${sharedCount} progress photo${sharedCount !== 1 ? "s" : ""}`);
            return;
          }
          // If none succeeded via share, fall through to download fallback
          console.warn("All share attempts failed, falling through to download");
        }
      }

      // Fallback: direct <a> downloads (works on desktop; on mobile at least the first download triggers)
      let savedCount = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const url = URL.createObjectURL(file);

        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        savedCount++;
        setPhotoProgress({ current: savedCount, total: files.length, phase: "saving" });

        // Brief delay between downloads
        await new Promise((r) => setTimeout(r, 500));
        URL.revokeObjectURL(url);
      }

      toast.success(`Saved ${savedCount} progress photo${savedCount !== 1 ? "s" : ""}`);
    } catch (err: any) {
      console.error("Failed to save photos:", err);
      toast.error(err?.message || "Failed to save progress photos");
    } finally {
      setIsSavingPhotos(false);
      setPhotoProgress({ current: 0, total: 0, phase: "" });
    }
  };

  return {
    isExporting,
    isSavingPhotos,
    photoProgress,
    exportData,
    saveAllProgressPhotos,
  };
}
